// Form Builder Types
//
// Pure type/interface declarations live in `@commenda/types/form-builder`.
// This file keeps the runtime-only symbols (const object, lookup arrays,
// helper functions) that stay UI-side so the types package has no runtime
// exports.

import type { ComponentName } from '@commenda/types';

/**
 * Standard context keys that are built-in and handled by the system.
 * These are the core Commenda OS entity IDs always available in form context.
 */
export const STANDARD_CONTEXT_KEYS = [
  'companyId',
  'corporationId',
  'directorId',
  'serviceRequestId',
  'transferPricingPolicyId',
  'transferPricingBenchmarkId',
] as const;

export const DEFAULT_REQUIRED_CONTEXT_KEYS = [
  'companyId',
  'corporationId',
  'directorId',
] as const;

// Component types supported by the form builder.
// `satisfies Record<string, ComponentName>` ensures every value here is a valid
// `ComponentName` — if you add a key, also add it to the union in `dsl.ts`.
export const ComponentType = {
  Alert: 'Alert',
  Input: 'Input',
  TextArea: 'TextArea',
  RadioGroup: 'RadioGroup',
  Select: 'Select',
  FileUpload: 'FileUpload',
  MultiFileUpload: 'MultiFileUpload',
  PhoneInput: 'PhoneInput',
  AddressInput: 'AddressInput',
  PasswordInput: 'PasswordInput',
  NumberInput: 'NumberInput',
  UrlInput: 'UrlInput',
  DateInput: 'DateInput',
  CheckboxGroup: 'CheckboxGroup',
  FormSummary: 'FormSummary',
  NestedForm: 'NestedForm',
  CountryInput: 'CountryInput',
  // Commenda OS components
  EntitySelect: 'EntitySelect',
  AutoDetectField: 'AutoDetectField',
  GovernmentIdentifier: 'GovernmentIdentifier',
  BankAccount: 'BankAccount',
  Location: 'Location',
  Document: 'Document',
  RegisteredAgent: 'RegisteredAgent',
  EntitySelectOrCreate: 'EntitySelectOrCreate',
  BusinessEntitySelectOrCreate: 'BusinessEntitySelectOrCreate',
  // Dynamic components
  DynamicFields: 'DynamicFields',
  KeyPersons: 'KeyPersons',
  DirectorDocument: 'DirectorDocument',
  Ownership: 'Ownership',
  IndirectTaxRegistration: 'IndirectTaxRegistration',
} as const satisfies Record<string, ComponentName>;

export type ComponentType = ComponentName;

// Components that occupy their entire module/section and reject any sibling
// components — the builder renders each as its own dedicated, locked section
// (a NestedForm, the KeyPersons picker, the Ownership cap-table node, or the
// IndirectTaxRegistration registration node).
export function isModuleLockingComponent(component?: string): boolean {
  return (
    component === ComponentType.NestedForm ||
    component === ComponentType.KeyPersons ||
    component === ComponentType.Ownership ||
    component === ComponentType.IndirectTaxRegistration
  );
}

// Helper to generate unique IDs
export const generateId = (prefix: string): string =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
